import React from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { apiClient } from '@/services/api';
import {
  ParticipantsTable,
  type TournamentParticipant,
} from '@/components/ParticipantsTable';
import { MainSection } from '@/components/layout/MainSection';
import { HeaderActionButton } from '@/components/ui';
import { useTournamentDetailContext } from '@/layouts/TournamentDetailLayout/context';
import type { ParticipantStats } from '@/layouts/TournamentDetailLayout/context';
import participantsStyles from '../TournamentParticipantsPage.module.scss';

const ParticipantsTab: React.FC = () => {
  const {
    tournament,
    tournamentId,
    canInvite,
    canChangeRoles,
    isMobile,
    setShowInviteModal,
    setBlockModalParticipant,
    handleRevokeInvite,
    handleDeleteParticipant,
    handleRoleChange,
    actionLoading,
    statusFilter,
    roleFilter,
    sortBy,
    sortOrder,
    participantsPage,
    participantsRowsPerPage,
    handleSortChange,
    handleStatusFilterChange,
    handleRoleFilterChange,
    setParticipantsPage,
    setParticipantsRowsPerPage,
  } = useTournamentDetailContext();

  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation(['tournament']);

  // Fetch participants data (same query key as layout to share cache)
  const {
    data: participantsData,
    isLoading: participantsLoading,
    error: participantsError,
  } = useQuery({
    queryKey: ['tournament-participants', tournamentId, statusFilter, roleFilter, sortBy, sortOrder, participantsPage, participantsRowsPerPage],
    queryFn: async () => {
      try {
        const [participantsResponse, statsResponse] = await Promise.all([
          apiClient.getTournamentParticipantsManagement(
            parseInt(tournamentId),
            {
              status: statusFilter === 'all' ? undefined : statusFilter,
              role: roleFilter === 'all' ? undefined : roleFilter,
              sortBy,
              sortOrder,
              page: participantsPage + 1,
              limit: participantsRowsPerPage,
            },
          ),
          apiClient.getTournamentParticipantsStats(parseInt(tournamentId)),
        ]);

        const apiData = participantsResponse.data?.data || participantsResponse.data;
        const participants = apiData?.participants || apiData;
        const pagination = apiData?.pagination;
        const statsData = statsResponse.data?.data || statsResponse.data;
        const stats = statsData?.stats || statsData;

        return {
          participants: Array.isArray(participants) ? participants as TournamentParticipant[] : [],
          stats: stats as ParticipantStats | null,
          totalCount: pagination?.total || (Array.isArray(participants) ? participants.length : 0),
        };
      } catch (err: any) {
        if (err.response?.status === 403) {
          return { participants: [], stats: null, totalCount: 0, accessDenied: true as const };
        }
        throw err;
      }
    },
    enabled: !!tournamentId && isAuthenticated,
    // Keep the previous result visible while a filter / sort / pagination
    // change refetches. Without this, the queryKey change flips isPending →
    // isLoading → true on every filter mutation, the `if (participantsLoading)
    // return null` branch unmounts MainSection + ParticipantsTable, and the
    // toolbar/header/footer remount when data arrives (visible page jump).
    // placeholderData makes only the rows re-render — toolbar stays mounted.
    placeholderData: keepPreviousData,
  });

  const participants = participantsData?.participants || [];
  const participantsTotalCount = participantsData?.totalCount || 0;

  if (participantsLoading) {
    return null;
  }

  if (participantsError) {
    return (
      <div className={participantsStyles.error}>
        <div className={participantsStyles.errorIcon}>!</div>
        <p className={participantsStyles.errorText}>{t('tournament:errorLoadingParticipants')}</p>
      </div>
    );
  }

  const headerRight = isMobile && canInvite ? (
    <HeaderActionButton icon="users-two" onClick={() => setShowInviteModal(true)} data-testid="tournament-invite-button">
      {t('tournament:invite')}
    </HeaderActionButton>
  ) : (
    <span className={participantsStyles.count}>{t('tournament:items', { count: participantsTotalCount })}</span>
  );

  return (
    <MainSection
      hideTitleOnDesktop
      title={tournament?.name || 'Tournament'}
      subtitle={t('tournament:participants')}
      logoUrl={tournament?.league?.logo_url || tournament?.leagueLogo}
      logoAlt={tournament?.league?.name || tournament?.leagueName}
      headerRight={headerRight}
    >
      <ParticipantsTable
        participants={participants}
        currentUserId={user?.id}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={handleSortChange}
        statusFilter={statusFilter}
        roleFilter={roleFilter}
        onStatusFilterChange={handleStatusFilterChange}
        onRoleFilterChange={handleRoleFilterChange}
        onBlockToggle={(participant) => setBlockModalParticipant(participant)}
        onRevokeInvite={handleRevokeInvite}
        onDeleteParticipant={handleDeleteParticipant}
        onRoleChange={canChangeRoles ? handleRoleChange : undefined}
        actionLoading={actionLoading}
        page={participantsPage}
        rowsPerPage={participantsRowsPerPage}
        totalCount={participantsTotalCount}
        onPageChange={setParticipantsPage}
        onRowsPerPageChange={setParticipantsRowsPerPage}
      />
    </MainSection>
  );
};

export default ParticipantsTab;
